import { initLWW } from "@referee-fyi/consistency";
import { type BaseIncident, INCIDENT_IGNORE } from "@referee-fyi/share";
import {
	type UseQueryResult,
	useMutation,
	useMutationState,
	useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { Match, type MatchData } from "robotevents";
import { toast } from "~components/Toast";
import { useShareConnection } from "~models/ShareConnection";
import { queryClient } from "~utils/data/query";
import { getShareProfile } from "~utils/data/share";
import {
	deleteIncident,
	editIncident,
	generateIncidentId,
	getDeletedIncidentsForEvent,
	getIncident,
	getIncidentsByEvent,
	getIncidentsByTeam,
	getManyIncidents,
	type Incident,
	type NewIncident,
	newIncident,
} from "../data/incident";
import type { HookQueryOptions } from "./robotevents";

export function useIncident(
	id: string | undefined | null,
	options?: HookQueryOptions<Incident | undefined>,
) {
	return useQuery<Incident | undefined>({
		queryKey: ["incidents", id],
		queryFn: () => {
			if (!id) {
				return undefined;
			}
			return getIncident(id);
		},
		staleTime: 0,
		refetchOnMount: "always",
		...options,
	});
}

export function useEventIncidents(sku: string | undefined | null) {
	return useQuery<Incident[]>({
		queryKey: ["incidents", "event", sku],
		queryFn: async () => {
			if (!sku) {
				return [];
			}
			return getIncidentsByEvent(sku);
		},
		staleTime: 0,
	});
}

export function useEventDeletedIncidents(sku: string | undefined | null) {
	return useQuery<Incident[]>({
		queryKey: ["incidents", "event", sku, "deleted"],
		queryFn: async () => {
			if (!sku) {
				return [];
			}
			const deleted = await getDeletedIncidentsForEvent(sku);
			const incidents = await getManyIncidents([...deleted]);
			return incidents.filter((i) => i !== undefined);
		},
		staleTime: 0,
	});
}

export function useNewIncident() {
	const connection = useShareConnection(["addIncident"]);
	return useMutation({
		mutationKey: ["newIncident"],
		mutationFn: async (incident: Omit<NewIncident, "time">) => {
			try {
				const time = new Date().toISOString();
				const { key: peer } = await getShareProfile();
				const result = await newIncident({
					data: { ...incident, time },
					peer,
					id: generateIncidentId(),
				});
				connection.addIncident(result);
				return result;
			} catch (e) {
				toast({
					type: "error",
					message: "Could not create new incident!",
					context: JSON.stringify(e),
				});
			}
		},
		onSettled: () => {
			return queryClient.invalidateQueries({ queryKey: ["incidents"] });
		},
	});
}

export function useEditIncident() {
	const connection = useShareConnection(["editIncident"]);
	return useMutation({
		mutationKey: ["editIncident"],
		mutationFn: async (incident: Omit<BaseIncident, "event" | "team">) => {
			try {
				const updated = await editIncident(incident.id, incident);
				connection.editIncident(updated!);
				return incident;
			} catch (e) {
				toast({
					type: "error",
					message: "Could not edit incident!",
					context: JSON.stringify(e),
				});
			}
		},
		onSettled: () => {
			return queryClient.invalidateQueries({ queryKey: ["incidents"] });
		},
	});
}

export function useDeleteIncident() {
	const connection = useShareConnection(["deleteIncident"]);
	return useMutation({
		mutationKey: ["deleteIncident"],
		mutationFn: async (id: string) => {
			try {
				await deleteIncident(id);
				connection.deleteIncident(id);
			} catch (e) {
				toast({
					type: "error",
					message: "Could not delete incident!",
					context: JSON.stringify(e),
				});
			}
		},
		onSettled: () => {
			return queryClient.invalidateQueries({ queryKey: ["incidents"] });
		},
	});
}

export function usePendingIncidents(
	predicate: (incident: Incident) => boolean,
) {
	const newIncident = useMutationState({
		filters: {
			mutationKey: ["newIncident"],
			status: "pending",
		},
		select: (mutation) => {
			const newIncident = mutation.state.variables as NewIncident;
			return initLWW<Incident>({
				value: { ...newIncident, id: `temp_incident_${mutation.mutationId}` },
				peer: "local",
				ignore: INCIDENT_IGNORE,
			});
		},
	});

	const editIncident = useMutationState({
		filters: {
			mutationKey: ["editIncident"],
			status: "pending",
		},
		select: (mutation) => mutation.state.variables as Incident,
	});

	const deleteIncident = useMutationState({
		filters: {
			mutationKey: ["deleteIncident"],
			status: "pending",
		},
		select: (mutation) => mutation.state.variables as string,
	});

	return {
		newIncident: newIncident.filter(predicate),
		editIncident: editIncident.filter(predicate),
		deleteIncident,
	};
}

export function useTeamIncidents(
	team: string | undefined | null,
	options?: HookQueryOptions<Incident[]>,
) {
	return useQuery<Incident[]>({
		queryKey: ["incidents", "team", team],
		queryFn: () => {
			if (!team) {
				return [];
			}
			return getIncidentsByTeam(team);
		},
		staleTime: 0,
		refetchOnMount: "always",
		...options,
	});
}

export function useTeamIncidentsByEvent(
	team: string | undefined | null,
	sku: string | undefined | null,
	options?: HookQueryOptions<Incident[]>,
) {
	return useQuery<Incident[]>({
		queryKey: ["incidents", "team", team, "event", sku],
		queryFn: async () => {
			if (!team || !sku) {
				return [];
			}

			const incidents = await getIncidentsByTeam(team);
			if (!sku || !incidents) {
				return [];
			}

			return incidents.filter((incident) => incident.event === sku);
		},
		staleTime: 0,
		refetchOnMount: "always",
		...options,
	});
}

export type InspectionStatus = "passed" | "failed" | "unknown";

export type InspectionResult = {
	status: InspectionStatus;
	incident: Incident | null;
};

export function getInspectionStatus(incidents: Incident[]): InspectionResult {
	const inspectionIncidents = incidents
		.filter(
			(i) =>
				i.outcome === "InspectionPassed" || i.outcome === "InspectionFailed",
		)
		.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

	if (inspectionIncidents.length === 0) {
		return { status: "unknown", incident: null };
	}

	const latest = inspectionIncidents[0];
	return {
		status: latest.outcome === "InspectionPassed" ? "passed" : "failed",
		incident: latest,
	};
}

export function useTeamInspectionStatus(
	team: string | undefined | null,
	sku: string | undefined | null,
	options?: HookQueryOptions<Incident[]>,
) {
	const { data: incidents } = useTeamIncidentsByEvent(team, sku, options);
	return useMemo(() => getInspectionStatus(incidents ?? []), [incidents]);
}

export type TeamIncidentsByMatch = {
	team: string;
	incidents: Incident[];
}[];

export function useTeamIncidentsByMatch(
	matchData?: MatchData | null,
	options?: HookQueryOptions<TeamIncidentsByMatch>,
): UseQueryResult<TeamIncidentsByMatch> {
	return useQuery({
		queryKey: ["incidents", "match", matchData],
		queryFn: async () => {
			if (!matchData) {
				return [];
			}

			const match = new Match(matchData);
			const alliances = [match.alliance("red"), match.alliance("blue")];
			const teams =
				alliances
					.flatMap((a) => a.teams.map((t) => t.team?.name))
					.filter((name): name is string => name !== undefined) ?? [];

			const incidentsByTeam: TeamIncidentsByMatch = [];

			for (const team of teams) {
				const incidents = (await getIncidentsByTeam(team)).filter(
					(i) => i.event === match.event.code,
				);

				incidentsByTeam.push({ team, incidents });
			}

			return incidentsByTeam;
		},
		...options,
	});
}
